import type { UnwrapNestedRefs } from 'vue';
import type { MoviePlayerState } from 'src/models/moviePlayer';
import type { PlaybackControlContext } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerPlaybackControl';
import type { MovieTransitionContext } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerMovieTransition';

export interface SeekContext {
  seekBySeekBar: (offsetMsec: number) => void;
  seekMovie: (offsetMsec: number) => Promise<void>;
  seekByTimeText: (timePoint: { dt: Date; dateDisp: string; timeDisp: string }) => Promise<void>;
}

// シーク（シークバー操作/時刻テキスト指定）を担うcomposable。
// 実際の動画切替はmovieTransition.setCurrentMovieに委譲し、
// 時刻シーク後の再生再開はcontrol.playUIに委譲する。
export function useMoviePlayerSeek(
  state: UnwrapNestedRefs<MoviePlayerState>,
  control: Pick<PlaybackControlContext, 'playUI'>,
  movieTransition: Pick<MovieTransitionContext, 'setCurrentMovie'>,
): SeekContext {
  const seekMovie = async(offsetMsec: number) => {
    if (!state.movieList.listSelection.currentMovieList) return;

    state.playback.isSeeking = true;
    // movieListのmovieについて順番に、offsetMsecのそのmovie内でのoffsetを算出する.
    // そのmovieのplayDuration内にoffsetが収まっていればそれが再生すべきmovie
    // となる. (最後のmovieについて、playEnd < hardEndでplayEnd以降の位置を
    // 指定されていた場合は最後のmovieを再生させたいのでそのようにしてある)
    let targetMovie;
    let targetMovieIdx;
    let targetMoviePlayOffsetMsec = 0;
    for (let i = 0, len = state.movieList.listSelection.currentMovieList.movies.length; i < len; i++) {
      const movie = state.movieList.listSelection.currentMovieList.movies[i];
      targetMovie = movie;
      targetMovieIdx = i;
      targetMoviePlayOffsetMsec = offsetMsec - (targetMovie.accumMsec || 0);
      if (movie.playDurationMsec === undefined || targetMoviePlayOffsetMsec < movie.playDurationMsec) {
        break;
      }
    }
    if (targetMovie === undefined || targetMovieIdx === undefined) {
      state.playback.isSeeking = false;
      return;
    }
    await movieTransition.setCurrentMovie(
      targetMovie,
      targetMovieIdx,
      targetMoviePlayOffsetMsec,
    );
    state.playback.isSeeking = false;
  };
  const seekBySeekBar = (offsetMsec: number) => {
    state.playback.seek.current = offsetMsec;
    seekMovie(offsetMsec);
  };
  const seekByTimeText = async(timePoint: { dt: Date; dateDisp: string; timeDisp: string }) => {
    state.playback.isSeeking = true;
    // movieListの開始と指定された時刻の差に、seek.minのずらし分を考慮
    let tmpOffset = timePoint.dt.getTime() - (state.movieList.listSelection.currentMovieList?.hardStartTs?.getTime() || 0);
    tmpOffset = Math.max(tmpOffset, 0);
    tmpOffset = Math.min(tmpOffset, (state.movieList.listSelection.currentMovieList?.fullDurationMsec || 0));
    const offsetMsec = tmpOffset + state.playback.seek.min;
    await seekMovie(offsetMsec);
    control.playUI();
    state.playback.isSeeking = false;
  };

  return {
    seekBySeekBar,
    seekMovie,
    seekByTimeText,
  };
}
